/**
 * phase2ViewMode.ts
 * The Phase-2 employee-planning view switcher's persisted mode.
 *
 * Modes:
 *   - 'staff'   — "Staff this job" (the reframe hero, PHASE2-REFRAME.md): a
 *                 job-centric people×weeks surface with per-task refine + mentor
 *                 nudge. The DEFAULT.
 *   - 'classic' — the legacy task-card weekly grid + capacity table.
 *   - 'board'   — the compact board presentation of the task grid.
 *   - 'roster'  — the standalone people×weeks roster timeline (the SAME shared
 *                 StaffRosterTimeline the Staff hero uses as its capacity hero,
 *                 rendered on its own without the task-staffing cards).
 *
 * Storage key is BUMPED to '.v2' so every existing user (who may have 'classic'
 * saved under the old key) lands in the new Staff view once, then their own
 * choice persists under the new key. A stored valid choice is always honored.
 * Bumped again to '.v3' (2026-08-04): the Staff hero's easy-to-hit "Switch to
 * Classic" link had stranded users in Classic — where index tests still staff
 * via the legacy bottom section — reading as a regression of the 2026-07-28
 * "index rows inside Tests on this job" work. Re-land everyone in Staff once.
 *
 * The storage read/decision are split so the pure resolver can be unit-tested
 * without a DOM.
 */

export type Phase2ViewMode = 'staff' | 'classic' | 'board' | 'roster';

/** Bumped '' → '.v2' → '.v3' — each bump re-lands everyone in Staff once. */
export const PHASE2_VIEW_MODE_KEY = 'phase2.viewMode.v3';

const VALID: readonly Phase2ViewMode[] = ['staff', 'classic', 'board', 'roster'];

/** True when `v` is one of the recognised view modes. */
export function isPhase2ViewMode(v: unknown): v is Phase2ViewMode {
  return typeof v === 'string' && (VALID as readonly string[]).includes(v);
}

/**
 * Pure decision: a stored value → the effective view mode. Any missing /
 * unrecognised value defaults to 'staff' (the reframe lands users in the hero;
 * legacy is one click away).
 */
export function resolveViewMode(stored: string | null | undefined): Phase2ViewMode {
  return isPhase2ViewMode(stored) ? stored : 'staff';
}

/** Read the remembered mode (tolerant of storage being unavailable). */
export function readPhase2ViewMode(): Phase2ViewMode {
  try {
    return resolveViewMode(localStorage.getItem(PHASE2_VIEW_MODE_KEY));
  } catch {
    return 'staff';
  }
}

/** Persist the chosen mode (best-effort). */
export function writePhase2ViewMode(mode: Phase2ViewMode): void {
  try {
    localStorage.setItem(PHASE2_VIEW_MODE_KEY, mode);
  } catch {
    /* storage unavailable */
  }
}
